import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, ToggleButtonGroup,
  ToggleButton, Alert, CircularProgress, Chip, Divider, LinearProgress
} from '@mui/material';
import { CheckCircle, CreditCard, ArrowBack } from '@mui/icons-material';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import config from '../config';
import { useAuth } from '../contexts/AuthContext';
import FlatPlanForm from './FlatPlanForm';

const PRICES = { monthly: '£40 / month', annual: '£400 / year' };

// ---------------------------------------------------------------------------
// Inner form — must live inside <Elements> so Stripe hooks work
// ---------------------------------------------------------------------------
interface CheckoutFormProps {
  plan: 'monthly' | 'annual';
  onSuccess: () => void;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({ plan, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [elementReady, setElementReady] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !elementReady) return;

    setProcessing(true);
    setError(null);

    const { error: submitError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/dashboard` },
      redirect: 'if_required',
    });

    if (submitError) {
      setError(submitError.message || 'Payment failed');
      setProcessing(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement onReady={() => setElementReady(true)} />
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={!stripe || processing || !elementReady}
        sx={{ mt: 3 }}
      >
        {processing || !elementReady
          ? <CircularProgress size={24} color="inherit" />
          : `Subscribe — ${PRICES[plan]}`}
      </Button>
    </form>
  );
};

// ---------------------------------------------------------------------------
// Plan picker + Stripe card form. Shared by the /payment-setup page and the
// profile page dialog; the caller decides what happens after a successful payment.
// ---------------------------------------------------------------------------
const PaymentSetupForm: React.FC<{ onSuccess: () => void; fixedPlans?: boolean }> = ({ onSuccess, fixedPlans }) => {
  const { token } = useAuth();
  const [plan, setPlan] = useState<'monthly' | 'annual'>('monthly');
  const [step, setStep] = useState<'select' | 'pay'>('select');
  const [stripePromise, setStripePromise] = useState<ReturnType<typeof loadStripe> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // null until known; true when a per-flat Stripe price is configured, which swaps in the per-flat plan card
  const [flatPricing, setFlatPricing] = useState<boolean | null>(null);

  useEffect(() => {
    axios.get(`${config.apiUrl}/api/payments/config`).then(res => {
      setStripePromise(loadStripe(res.data.publishable_key));
      setFlatPricing(Boolean(res.data.flat_pricing));
    });
  }, []);

  const handleContinue = async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    setClientSecret(null);

    try {
      const res = await axios.post(`${config.apiUrl}/api/payments/create-subscription`, { plan }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClientSecret(res.data.client_secret);
      setStep('pay');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to set up payment');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlan = () => {
    setStep('select');
    setClientSecret(null);
    setError(null);
  };

  if (flatPricing === null) return <LinearProgress sx={{ borderRadius: 1 }} />;
  if (flatPricing && !fixedPlans) return <FlatPlanForm stripePromise={stripePromise} onSuccess={onSuccess} />;

  return (
    <>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <CreditCard sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h5" component="h2" fontWeight={700}>Choose your plan</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          You'll be charged immediately. Cancel anytime from your profile.
        </Typography>
      </Box>

      {['Full access to all features', 'AI certificate extraction', 'Expiry reminders & Gmail integration'].map(perk => (
        <Box key={perk} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />
          <Typography variant="body2">{perk}</Typography>
        </Box>
      ))}

      <Divider sx={{ my: 3 }} />

      {step === 'select' ? (
        <>
          <Typography variant="subtitle2" fontWeight={600} mb={1}>Select plan</Typography>
          <ToggleButtonGroup
            value={plan}
            exclusive
            onChange={(_, val) => val && setPlan(val)}
            fullWidth
            sx={{ mb: 3 }}
          >
            <ToggleButton value="monthly" sx={{ py: 1.5 }}>
              <Box>
                <Typography fontWeight={600}>Monthly</Typography>
                <Typography variant="body2" color="text.secondary">£40 / month</Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="annual" sx={{ py: 1.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography fontWeight={600}>Annual</Typography>
                  <Chip label="Best value" size="small" color="success" />
                </Box>
                <Typography variant="body2" color="text.secondary">£400 / year</Typography>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleContinue}
            disabled={loading}
          >
            {loading
              ? <CircularProgress size={24} color="inherit" />
              : `Continue with ${plan === 'monthly' ? 'Monthly' : 'Annual'} — ${PRICES[plan]}`}
          </Button>
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {plan === 'monthly' ? 'Monthly — £40 / month' : 'Annual — £400 / year'}
            </Typography>
            <Button size="small" startIcon={<ArrowBack />} onClick={handleChangePlan}>
              Change
            </Button>
          </Box>

          {loading ? (
            <>
              <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
              <Box sx={{
                bgcolor: 'grey.50', borderRadius: 1, p: 3, textAlign: 'center',
                border: '1px solid', borderColor: 'grey.200',
              }}>
                <Typography variant="body2" color="text.secondary">Preparing payment form…</Typography>
              </Box>
            </>
          ) : clientSecret && stripePromise ? (
            <Elements stripe={stripePromise} options={{ clientSecret }} key={clientSecret}>
              <CheckoutForm plan={plan} onSuccess={onSuccess} />
            </Elements>
          ) : null}
        </>
      )}
    </>
  );
};

export default PaymentSetupForm;
